/**
 * ============================================================================
 * PROFILE PAGE - Dashboard-style layout (green/black, compact, same as Dashboard)
 * ============================================================================
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useUserStore } from '../store';
import { useAuthContext } from '../providers';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Input from '../components/common/Input';
import Textarea from '../components/common/Textarea';
import Spinner from '../components/common/Spinner';
import Avatar from '../components/common/Avatar';
import UserProfile from '../components/user/UserProfile';
import UserStats from '../components/user/UserStats';
import UserVerification from '../components/user/UserVerification';
import UserActivity from '../components/user/UserActivity';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Profile = () => {
  const { user } = useAuthContext();
  const { profile, loading: storeLoading } = useUserStore();
  const { loadMyProfile, updateProfile, uploadProfilePhoto, deleteProfilePhoto, loading } = useUser();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const { language } = useI18n();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', bio: '' });
  const [isLoading, setIsLoading] = useState(true);
  const statsInView = true;
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await loadMyProfile();
      } catch (error) {
        showError('Failed to load profile');
        console.error('Load profile error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [loadMyProfile, showError]);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || profile.fullName || '',
        email: profile.email || '',
        bio: profile.bio || '',
      });
    }
  }, [profile]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await updateProfile(formData);
      showSuccess(language === 'fr' ? 'Profil mis à jour!' : 'Profile updated!');
      setIsEditing(false);
      await loadMyProfile();
    } catch (error) {
      showError(error?.message || (language === 'fr' ? 'Échec de la mise à jour' : 'Update failed'));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await uploadProfilePhoto(file);
      showSuccess(language === 'fr' ? 'Photo mise à jour!' : 'Photo updated!');
      await loadMyProfile();
    } catch (error) {
      showError(error?.message || (language === 'fr' ? 'Échec du téléchargement' : 'Upload failed'));
    }
  };

  const handlePhotoDelete = async () => {
    try {
      await deleteProfilePhoto();
      showSuccess(language === 'fr' ? 'Photo supprimée!' : 'Photo removed!');
      await loadMyProfile();
    } catch (error) {
      showError(error?.message || (language === 'fr' ? 'Échec de la suppression' : 'Remove failed'));
    }
  };

  if (isLoading || storeLoading) {
    return (
      <UserDashboardLayout hideFooter>
        <div className="dashboard-wt dashboard-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement du profil...' : 'Loading profile...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  const displayUser = profile ?? user;
  const displayName = displayUser?.fullName || displayUser?.name || (language === 'fr' ? 'Utilisateur' : 'User');
  const firstName = displayUser?.fullName?.split(' ')[0] || displayUser?.name?.split(' ')[0] || user?.fullName?.split(' ')[0] || user?.name?.split(' ')[0] || (language === 'fr' ? 'Utilisateur' : 'User');
  const isVerified = profile?.verificationLevel === 'verified';

  const tabs = [
    { id: 'profile', icon: '👤', label: language === 'fr' ? 'Profil' : 'Profile' },
    { id: 'stats', icon: '📊', label: language === 'fr' ? 'Statistiques' : 'Stats' },
    { id: 'verification', icon: '🆔', label: language === 'fr' ? 'Vérification' : 'Verification' },
    { id: 'activity', icon: '📈', label: language === 'fr' ? 'Activité' : 'Activity' },
  ];

  return (
    <UserDashboardLayout hideFooter>
      <div className="dashboard-wt dashboard-wt-fit-viewport">
        {/* Hero - same style as Dashboard */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner profile-hero-with-avatar">
            <div className={`profile-hero-avatar-ring ${isVerified ? 'profile-hero-avatar-verified' : ''} relative inline-block`}>
              <Avatar
                src={profile?.photoUrl}
                name={displayName}
                size="lg"
                variant="circle"
              />
              <label className="profile-photo-upload-ring" title={language === 'fr' ? 'Changer la photo' : 'Change photo'}>
                <span className="sr-only">{language === 'fr' ? 'Changer la photo' : 'Change photo'}</span>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /></svg>
              </label>
            </div>
            <div>
              <h1 className="dashboard-wt-hero-title">
                {language === 'fr' ? 'Mon profil' : 'My Profile'}, <span>{firstName}</span>
              </h1>
              <p className="dashboard-wt-hero-sub">
                {profile?.email || (language === 'fr' ? 'Membre WekaTrust' : 'WekaTrust Member')}
                {isVerified && ` · ✓ ${language === 'fr' ? 'Vérifié' : 'Verified'}`}
              </p>
              {profile?.photoUrl && (
                <button type="button" onClick={handlePhotoDelete} className="text-sm text-[#a3a3a3] hover:text-[#f87171] mt-1 transition-colors">
                  {language === 'fr' ? 'Supprimer la photo' : 'Remove photo'}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Tabs as stat-style pills (same visual language as Dashboard stats) */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {tabs.map((tab, i) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`dashboard-wt-stat-card profile-tab-stat ${activeTab === tab.id ? 'profile-tab-stat-active' : ''}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{tab.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab content - Profile */}
        {activeTab === 'profile' && (
          <section className="dashboard-wt-section">
            <div className="dashboard-wt-section-inner">
              <div className="dashboard-wt-grid">
                <div className="dashboard-wt-card dashboard-wt-card-large">
                  <div className="dashboard-wt-card-header profile-card-header-flex">
                    <h2 className="dashboard-wt-card-title">
                      {language === 'fr' ? 'Informations personnelles' : 'Personal Information'}
                    </h2>
                    {!isEditing && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={() => setIsEditing(true)}
                      >
                        ✏️ {language === 'fr' ? 'Modifier' : 'Edit'}
                      </button>
                    )}
                  </div>
                  <div className="dashboard-wt-card-content">
                    {isEditing ? (
                      <div className="space-y-3">
                        <Input
                          label={language === 'fr' ? 'Nom' : 'Name'}
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.12)] !text-[#fafafa] placeholder:!text-[#737373]"
                        />
                        <Input
                          label={language === 'fr' ? 'Email' : 'Email'}
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.12)] !text-[#fafafa] placeholder:!text-[#737373]"
                        />
                        <Textarea
                          label={language === 'fr' ? 'Bio' : 'Bio'}
                          value={formData.bio}
                          onChange={(e) => handleChange('bio', e.target.value)}
                          rows={3}
                          placeholder={language === 'fr' ? 'Parlez-nous de vous...' : 'Tell us about yourself...'}
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.12)] !text-[#fafafa] placeholder:!text-[#737373]"
                        />
                        <div className="flex gap-2 flex-wrap">
                          <button type="button" className="dashboard-wt-btn-primary" onClick={handleSave} disabled={loading}>
                            {loading ? <><Spinner size="sm" className="mr-2 inline" />{language === 'fr' ? 'Enregistrement...' : 'Saving...'}</> : `💾 ${language === 'fr' ? 'Enregistrer' : 'Save'}`}
                          </button>
                          <button
                            type="button"
                            className="dashboard-wt-btn-secondary"
                            onClick={() => {
                              setIsEditing(false);
                              setFormData({ name: profile?.name || profile?.fullName || '', email: profile?.email || '', bio: profile?.bio || '' });
                            }}
                          >
                            {language === 'fr' ? 'Annuler' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <UserProfile user={profile} showActions={false} />
                        {!profile?.bio && (
                          <button type="button" className="dashboard-wt-btn-secondary mt-2" onClick={() => setIsEditing(true)}>
                            {language === 'fr' ? 'Écrire ma bio' : 'Write my bio'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'stats' && (
          <section className="dashboard-wt-section">
            <div className="dashboard-wt-section-inner">
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{language === 'fr' ? 'Statistiques' : 'Stats'}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <UserStats />
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'verification' && (
          <section className="dashboard-wt-section">
            <div className="dashboard-wt-section-inner">
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{language === 'fr' ? 'Vérification' : 'Verification'}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <UserVerification onVerify={() => navigate('/verification')} />
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'activity' && (
          <section className="dashboard-wt-section">
            <div className="dashboard-wt-section-inner">
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{language === 'fr' ? 'Activité récente' : 'Recent Activity'}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <UserActivity period="month" />
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </UserDashboardLayout>
  );
};

export default Profile;
